import { inject, Injectable } from '@angular/core';
import {
  SupplierCreditNoteService,
  RouteEntitySubscriberService,
  SupplierCreditNoteItemService,
  SupplierCreditNoteItemMappingsService,
  ClientCreditNoteDraftItemService,
  ClientCreditNoteItemService,
  SupplierCreditNoteItem,
  SupplierCreditNoteItemMapping,
  ClientCreditNoteDraftItem,
  ClientCreditNoteItem,
  SupplierCreditNote,
  findSectionIriByEntityIri,
  setSectionValues,
} from 'shared';
import { take } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SupplierCreditNoteCoordinator {
  #tracker = inject(RouteEntitySubscriberService);
  #creditNoteService = inject(SupplierCreditNoteService);
  #itemService = inject(SupplierCreditNoteItemService);
  #mappingService = inject(SupplierCreditNoteItemMappingsService);
  #draftItemService = inject(ClientCreditNoteDraftItemService);
  #linkedItemService = inject(ClientCreditNoteItemService);

  #creditNoteSig = this.#tracker.getSignal<SupplierCreditNote>('supplierCreditNote');
  #itemsBySectionSig = this.#tracker.getSectionItemsSignal<SupplierCreditNoteItem>('supplierCreditNote');
  #mappingsBySectionSig = this.#tracker.getItemMappingsSignal<SupplierCreditNoteItemMapping>('supplierCreditNote');
  #draftsBySectionSig = this.#tracker.getDraftItemsSignal<ClientCreditNoteDraftItem>('supplierCreditNote');
  #linkedItemsBySectionSig = this.#tracker.getLinkedItemsSignal<ClientCreditNoteItem>('supplierCreditNote');

  #recompute?: () => void;
  setRecompute(fn: () => void) {
    this.#recompute = fn;
  }

  #creditNoteIri(): string | undefined {
    return this.#creditNoteSig()?.['@id'];
  }

  refreshCreditNoteEntity() {
    const iri = this.#creditNoteIri();
    if (!iri) return;
    this.#creditNoteService
      .get(iri)
      .pipe(take(1))
      .subscribe((cn) => this.#creditNoteSig.set(cn));
  }

  refreshTotals(): void {
    this.refreshCreditNoteEntity();
  }

  refreshCreditNote(): void {
    const iri = this.#creditNoteIri();
    if (!iri) return;

    forkJoin({
      items: this.#itemService.getForSupplierCreditNote(iri).pipe(take(1)),
      mappings: this.#mappingService.getForSupplierCreditNote(iri).pipe(take(1)),
      drafts: this.#draftItemService.getForSupplierCreditNote(iri).pipe(take(1)),
      linked: this.#linkedItemService.getForSupplierCreditNote(iri).pipe(take(1)),
    }).subscribe(({ items, mappings, drafts, linked }) => {
      this.#itemsBySectionSig.set(items);
      this.#mappingsBySectionSig.set(mappings);
      this.#draftsBySectionSig.set(drafts);
      this.#linkedItemsBySectionSig.set(linked);
      this.#recompute?.();
    });
  }

  refreshItems() {
    const iri = this.#creditNoteIri();
    if (!iri) return;
    this.#itemService
      .getForSupplierCreditNote(iri)
      .pipe(take(1))
      .subscribe((sections) => this.#itemsBySectionSig.set(sections));
  }

  refreshSectionItem(itemIri: string) {
    const bySection = this.#itemsBySectionSig();
    const sectionIri = findSectionIriByEntityIri(bySection, itemIri) ?? undefined;
    if (sectionIri)
      this.refreshSectionMappings();
    else {
      this.refreshItems();
      return;
    }

    // rowFirst<SupplierCreditNoteItem>(
    //   (iri) => (this.#itemService as any).get(iri),
    //   itemIri,
    //   (fresh) => upsertInSection(this.#itemsBySectionSig, sectionIri, fresh),
    //   () =>
    // );
  }

  refreshSectionMappings() {
    const iri = this.#creditNoteIri();
    if (!iri) return;
    this.#mappingService
      .getForSupplierCreditNote(iri)
      .pipe(take(1))
      .subscribe((sections) => this.#mappingsBySectionSig.set(sections));
  }

  refreshItemMappingForLine(itemIri: string) {
    const iri = this.#creditNoteIri();
    if (!iri) return;
    this.#mappingService
      .getForSupplierCreditNoteItem(itemIri)
      .pipe(take(1))
      .subscribe((list) => {
        this.#mappingsBySectionSig.update((sections) => {
          const current = (sections?.[iri] ?? []) as SupplierCreditNoteItemMapping[];
          const kept = current.filter((m) => m.supplierCreditNoteItem !== itemIri);
          return setSectionValues(sections, iri, [...kept, ...list]);
        });
        this.#recompute?.();
      },
      (_) => this.refreshSectionMappings()
    );
  }

  refreshDraftItems(): void {
    const iri = this.#creditNoteIri();
    if (!iri) return;
    this.#draftItemService
      .getForSupplierCreditNote(iri)
      .pipe(take(1))
      .subscribe((sections) => this.#draftsBySectionSig.set(sections));
  }

  refreshLinkedItems(): void {
    const iri = this.#creditNoteIri();
    if (!iri) return;
    this.#linkedItemService
      .getForSupplierCreditNote(iri)
      .pipe(take(1))
      .subscribe((sections) => this.#linkedItemsBySectionSig.set(sections));
  }
}
